/* eslint-disable react-hooks/exhaustive-deps */
import { addDays, isSameDay, parseISO, startOfWeek } from "date-fns";
import { TFunction } from "i18next";
import { useCallback,useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { getDayNumber } from "../../scheduler/ui/dialog/copy-shifts-dialog/utils";
import { getFTShiftDemands, resetPostShiftDemandStatus } from "../store/shift-demand-slice";
import { FTShiftDemand, Job, PostDemandShift, Workcenter, WorkcenterRow } from "../store/shift-demand-types";
import useShiftDemandData from "./use-shift-demand-data";

const groupShiftsByWorkcenterAndDay = (
	workcenters: Workcenter[],
	shifts: FTShiftDemand[],
	startDate: Date,
	jobNameMap: Map<number, string>,
	shiftPatternNameMap: Map<number, string>,
	t: TFunction
): WorkcenterRow[] => {
	return workcenters.map((wc) => {
		const wcShifts = shifts.filter((shift) => shift.workcenterId === wc.id);
		const transformedJobs: Job[] = [];

		wcShifts.forEach((shift) => {
			let job = transformedJobs.find((j) => j.jobId === shift.jobId);
			if (!job) {
				job = {
					jobId: shift.jobId,
					jobName:
						jobNameMap.get(shift.jobId) ||
						t("shift-demand_job-id-fallback", { id: shift.jobId }),
					shiftPatterns: [],
				};
				transformedJobs.push(job);
			}

			let shiftPattern = job.shiftPatterns.find(
				(sp) => sp.shiftPatternId === shift.shiftPatternId
			);
			if (!shiftPattern) {
				shiftPattern = {
					shiftPatternId: shift.shiftPatternId,
					shiftPatternName:
						shiftPatternNameMap.get(shift.shiftPatternId) ||
						t("shift-demand_pattern-id-fallback", {
							id: shift.shiftPatternId,
						}),
					dailyDemands: [], // Initialize with new structure
                    startDateTime: shift.startDateTime,
                    endDateTime: shift.endDateTime,
				};
				job.shiftPatterns.push(shiftPattern);
			}

			const shiftDate = parseISO(shift.startDateTime);
			const dayIndex = isSameDay(shiftDate, startDate)
				? 0
				: Math.floor((+shiftDate - +startDate) / (1000 * 60 * 60 * 24));

			if (dayIndex >= 0 && dayIndex < 7) {
					shiftPattern.dailyDemands.push({
						dayIndex: dayIndex,
						workersRequired: shift.workersRequired ?? 0,
						shiftId: shift.shiftId,
						demandId: `${wc.id}-${job.jobId}-${shiftPattern.shiftPatternId}-${dayIndex}`,
                        shiftStartDateTime: shift.startDateTime,
					});
			}
		});

		return {
			workcenterId: wc.id,
			workcenterName: wc.name,
			jobs: transformedJobs,
		};
	});
};


const useShiftDemandController = () => {
	const [updatedShiftDemands, setUpdatedShiftDemands] = useState<WorkcenterRow[]>([]);
	const [formattedShiftDemands, setFormattedShiftDemands] =  useState<WorkcenterRow[]>([]);
	const [editedDemands, setEditedDemands] = useState<PostDemandShift[]>([]);
	const [searchText , setSearchText] = useState<string>("");

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { shiftDemands, filterData, selectedLocation, FTShiftCurrentDate, weekStartDay, getFullTimeShiftsMetaStatus } = useShiftDemandData();
	const [currentDate, setCurrentDate] = useState<string>(FTShiftCurrentDate ? FTShiftCurrentDate : new Date().toISOString());
	const startingDay = useMemo(() => {
			return startOfWeek(new Date(new Date(currentDate).setHours(0, 0, 0, 0)), {
			weekStartsOn: getDayNumber(weekStartDay),
			});
		}, [currentDate, weekStartDay]);

	const originalDemandsMap = useMemo(() => {
        if (!shiftDemands) {
            return new Map<string, number>();
        }
        return new Map(
            shiftDemands.map(shift => [`${shift.workcenterId}-${shift.shiftId}-${shift.jobId}`, shift.workersRequired ?? 0])
        );
    }, [shiftDemands]);

	const { allWorkcenters, jobs, shiftPatterns } = filterData || {};

	const selectedLocationWorkcenters = useMemo(() => {
		return allWorkcenters?.filter((workcenter) => workcenter.locationId === selectedLocation)
			?.sort((a, b) => a.name.localeCompare(b.name)) || [];
	}, [allWorkcenters, selectedLocation]);

	const dateRange = useMemo(
		() => {
		
		return {
			startDate: startOfWeek(
			new Date(
				new Date(currentDate).setHours(0, 0, 0, 0)
			),
			{
				weekStartsOn: getDayNumber(weekStartDay)
			}
			),
			endDate: addDays(
			startOfWeek(
				new Date(
				new Date(currentDate).setHours(0, 0, 0, 0)
				),
				{
				weekStartsOn: getDayNumber(weekStartDay)
				}
			),
			7
			)
		}},
	[currentDate, weekStartDay]
	);

	useEffect(() => {
		dispatch(
			getFTShiftDemands({
				startDate: dateRange?.startDate?.toISOString(),
				endDate: dateRange?.endDate?.toISOString(),
			})
		);
	}, [dispatch, dateRange, selectedLocation]);

    // reset edited demands when date range changes or selectedLocation changes or post demands change without saving
    useEffect(() => {
        setEditedDemands([]);
    }
    , [dateRange, selectedLocation]);


    useEffect(() => {
      if (
        getFullTimeShiftsMetaStatus === "fulfilled" ||
        getFullTimeShiftsMetaStatus === "error"
      ) {
        setEditedDemands([]);
      }
    }, [getFullTimeShiftsMetaStatus]);

    const handleDemandChange = useCallback(
        (demandId: string, value: number, workcenterId: number, jobId: number, shiftId: string,) => {
			const compositeKey = `${workcenterId}-${shiftId}-${jobId}`;
            const originalValue = originalDemandsMap.get(compositeKey) ?? 0;
            setEditedDemands((prev) => {
                const existingDemandIndex = prev.findIndex((d) => d.demandId === demandId);

                // If the new value is the same as the original...
                if (value === originalValue) {
                    // ...and it exists in our edited list, remove it.
                    if (existingDemandIndex > -1) {
                        return prev.filter((d) => d.demandId !== demandId);
                    }
                    return prev;
                }
                
                // If the new value is different from the original...
                if (existingDemandIndex > -1) {
                    // ...update it in the list.
                    return prev.map((d) =>
                        d.demandId === demandId
                            ? { ...d, workersRequired: value, workcenterId, jobId, shiftId }
                            : d
                    );
                } else {
                    // ...add it to the list.
                    return [
                        ...prev,
                        { demandId, workersRequired: value, workcenterId, jobId, shiftId },
                    ];
                }
            });
        },
            [originalDemandsMap]
        );


	const transformedDemands = useMemo(() => {
		const jobNameMap = new Map(jobs?.map((job: any) => [job.id, job.name]));
		const shiftPatternNameMap = new Map(
			shiftPatterns?.map((sp: any) => [sp.id, sp.name])
		);

		if (selectedLocationWorkcenters?.length > 0) {
			return groupShiftsByWorkcenterAndDay(
				selectedLocationWorkcenters,
				shiftDemands || [],
				dateRange?.startDate,
				jobNameMap,
				shiftPatternNameMap,
				t
			);
		}
		return [];
	}, [
		selectedLocationWorkcenters,
		shiftDemands,
		dateRange?.startDate,
		jobs,
		shiftPatterns,
		t,
	]);

	useEffect(() => {
		setUpdatedShiftDemands(transformedDemands);
		setFormattedShiftDemands(transformedDemands);
	}, [transformedDemands]);

    useEffect(() => {
		if(searchText !== ""){
		const data = getSearchFilterData(formattedShiftDemands, searchText);
		setUpdatedShiftDemands(data)
		} else {
			setUpdatedShiftDemands(formattedShiftDemands);
		}
	},[searchText, formattedShiftDemands]);

	
	const getSearchFilterData = (data: WorkcenterRow[], searchQuery: string): WorkcenterRow[] => {
		const lowerSearch = searchQuery.toLowerCase();
		return data 
		.map((workcenter) => {
			const workcenterMatch = workcenter.workcenterName.toLowerCase().includes(lowerSearch);
			const filteredJobs = workcenter.jobs
			.map((job) => {
			const jobMatch = job.jobName.toLowerCase().includes(lowerSearch);
				const filteredShiftPatterns = job.shiftPatterns?.filter((pattern) =>
				pattern.shiftPatternName.toLowerCase().includes(lowerSearch)
				) || [];
	
				// If job matches, returning full job (with all patterns)
				if (jobMatch) {
				return job;
				}
				// If any shift pattern matched, returning only matched patterns
				if (filteredShiftPatterns.length > 0) {
				return {
					...job,
					shiftPatterns: filteredShiftPatterns,
				};
				}
	
				return null;
			})
			.filter((job): job is Job => job !== null);
	
			// If workcenter matched, return full object
			if (workcenterMatch) {
			return workcenter;
			}
	
			// If any jobs matched, return only those
			if (filteredJobs.length > 0) {
			return {
				...workcenter,
				jobs: filteredJobs,
			};
			}
	
			return null;
		})
		.filter((wc): wc is WorkcenterRow => wc !== null);
	};

    const getRangeForSelectedDate = (selectedDate: Date) => {
      const startDate = startOfWeek(
        new Date(selectedDate.setHours(0, 0, 0, 0)),
        {
          weekStartsOn: getDayNumber(weekStartDay),
        }
      );
      const endDate = addDays(startDate, 7);
      return {
        startDate,
        endDate,
      };
    };

	useEffect(() => {
		// close snackbar if opened already
		dispatch(resetPostShiftDemandStatus());
	},[dispatch])

	


	return [
		{
			updatedShiftDemands,
            editedDemands,
			searchText,
			dateRange,
			currentDate,
			startingDay,
		},
		{
			setSearchText,
            setEditedDemands,
            handleDemandChange,
			getRangeForSelectedDate,
			setCurrentDate,
		},
	] as const;
};

export default useShiftDemandController;
